(() => {
  const initializedTracks = new WeakSet();
  const journeyWheelCleanups = new WeakMap();
  const desktop = () => window.matchMedia('(min-width: 750px)').matches;
  const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  function timelineParts(root) {
    const track = root.querySelector('[data-kh-journey-track]');
    const items = [...root.querySelectorAll('[data-kh-journey-item]')];
    const center = track.getBoundingClientRect().left + track.clientWidth / 2;
    const distances = items.map(item => Math.abs(item.getBoundingClientRect().left + item.clientWidth / 2 - center));
    const savedIndex = Math.max(0, Math.min(items.length - 1, Number(root.dataset.khJourneyIndex) || 0));
    const active = desktop() && root.dataset.khJourneyDesktop === 'true'
      ? distances.indexOf(Math.min(...distances)) : savedIndex;
    return {track, items, active};
  }

  function updateTimeline(root) {
    const {track, items, active} = timelineParts(root);
    root.dataset.khJourneyIndex = String(active);
    items.forEach((item, index) => {
      item.classList.toggle('is-active', index === active);
      item.classList.toggle('is-complete', index < active);
    });
    root.querySelectorAll('[data-kh-journey-dot]').forEach((dot, index) => {
      dot.classList.toggle('is-active', index === active);
      dot.setAttribute('aria-current', index === active ? 'step' : 'false');
    });
    root.querySelector('[data-kh-journey-prev]').disabled = active <= 0;
    root.querySelector('[data-kh-journey-next]').disabled = active >= items.length - 1;
    if (!desktop()) updateMobileProgress(track, items, active);
  }

  function updateMobileProgress(track, items, active) {
    // Animate one continuous rail, including when milestone heights differ.
    const trackRect = track.getBoundingClientRect();
    const dots = items.map(item => item.querySelector('.kh-journey__dot').getBoundingClientRect());
    const centers = dots.map(dot => dot.top + dot.height / 2 - trackRect.top);
    const start = centers[0];
    const length = centers[centers.length - 1] - start;
    const end = active < items.length - 1 ? (centers[active] + centers[active + 1]) / 2 : centers[active];
    track.style.setProperty('--kh-journey-rail-top', `${start}px`);
    track.style.setProperty('--kh-journey-rail-left', `${dots[0].left + dots[0].width / 2 - trackRect.left - 1}px`);
    track.style.setProperty('--kh-journey-rail-height', `${length}px`);
    track.style.setProperty('--kh-journey-progress', length > 0 ? String((end - start) / length) : '0');
    track.dataset.khJourneyProgress = 'true';
  }

  function goToMilestone(root, index, behavior = motion()) {
    const {track, items} = timelineParts(root);
    const targetIndex = Math.max(0, Math.min(items.length - 1, index));
    const item = items[targetIndex];
    if (!item) return;
    if (!desktop()) {
      root.dataset.khJourneyIndex = String(targetIndex);
      updateTimeline(root);
      return;
    }
    const offset = item.getBoundingClientRect().left + item.clientWidth / 2 - track.getBoundingClientRect().left - track.clientWidth / 2;
    track.scrollTo({left: track.scrollLeft + offset, behavior});
  }

  function normalizedWheelDelta(event) {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 16;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * window.innerHeight;
    return event.deltaY;
  }

  function journeyWheelLockState(root, direction, delta) {
    const rect = root.getBoundingClientRect();
    const alignment = rect.top + rect.height / 2 - window.innerHeight / 2;
    const centerTolerance = Math.min(80, window.innerHeight * 0.08);
    const centered = Math.abs(alignment) <= centerTolerance;
    const crossesCenter = direction > 0
      ? alignment > 0 && alignment - delta <= 0
      : alignment < 0 && alignment - delta >= 0;
    return {capture: centered || crossesCenter, alignment};
  }

  function initializeJourneyWheel(root) {
    if (journeyWheelCleanups.has(root)) return;

    let targetIndex = null;
    let accumulatedDelta = 0;
    let previousDirection = 0;
    let lastWheelAt = 0;
    let nextStepAt = 0;

    const resetWheel = () => {
      targetIndex = null;
      accumulatedDelta = 0;
      previousDirection = 0;
      nextStepAt = 0;
    };

    const onWheel = event => {
      if (!desktop() || event.ctrlKey || event.defaultPrevented || !event.cancelable) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

      const delta = normalizedWheelDelta(event);
      if (!delta) return;

      const direction = delta > 0 ? 1 : -1;
      const lockState = journeyWheelLockState(root, direction, delta);
      if (!lockState.capture) {
        resetWheel();
        return;
      }

      const {track, items, active} = timelineParts(root);
      if (items.length < 2 || track.scrollWidth <= track.clientWidth + 2) return;

      const now = performance.now();
      if (now - lastWheelAt > 700) targetIndex = null;
      if (direction !== previousDirection || now - lastWheelAt > 200) accumulatedDelta = 0;
      // Reversing the wheel responds immediately, even during a transition.
      if (direction !== previousDirection) nextStepAt = 0;
      previousDirection = direction;
      lastWheelAt = now;

      const currentIndex = targetIndex === null ? active : targetIndex;
      const nextIndex = currentIndex + direction;
      if ((nextIndex < 0 || nextIndex >= items.length) && now >= nextStepAt) {
        resetWheel();
        return;
      }

      event.preventDefault();
      if (lockState.alignment) {
        window.scrollTo({top: window.scrollY + lockState.alignment, behavior: 'instant'});
      }

      accumulatedDelta += Math.abs(delta);
      // Rate-limit steps, rather than waiting for the wheel to stop completely.
      if (now < nextStepAt || accumulatedDelta < 48) return;

      accumulatedDelta = 0;
      targetIndex = nextIndex;
      nextStepAt = now + 550;
      goToMilestone(root, nextIndex);
    };

    window.addEventListener('wheel', onWheel, {passive: false});
    journeyWheelCleanups.set(root, () => {
      window.removeEventListener('wheel', onWheel);
      journeyWheelCleanups.delete(root);
    });
  }


  function updateReasonNavigation(root) {
    const navigation = root.querySelector('[data-kh-why-navigation]');
    if (!navigation) return;
    const cards = [...root.querySelectorAll('[data-kh-why-card]')];
    const active = Math.max(0, cards.findIndex(card => card.classList.contains('is-active')));
    navigation.hidden = desktop();
    navigation.querySelector('[data-kh-why-prev]').disabled = active === 0;
    navigation.querySelector('[data-kh-why-next]').disabled = active === cards.length - 1;
    const current = navigation.querySelector('[data-kh-why-current]');
    if (current.textContent !== String(active + 1)) current.textContent = String(active + 1);
  }

  function revealReason(card, behavior = motion()) {
    const list = card.closest('[data-kh-why-list]');
    if (list.scrollWidth > list.clientWidth) {
      const inset = parseFloat(getComputedStyle(list).scrollPaddingLeft) || 0;
      list.scrollTo({left: list.scrollLeft + card.getBoundingClientRect().left - list.getBoundingClientRect().left - inset, behavior});
    }
  }

  function activateReason(card) {
    const list = card.closest('[data-kh-why-list]');
    list.querySelectorAll('[data-kh-why-card]').forEach(item => {
      const active = item === card;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-expanded', String(active));
    });
    updateReasonNavigation(card.closest('.kh-why'));
    revealReason(card);
  }

  function initialize() {
    document.querySelectorAll('.kh-our-story .kh-why').forEach(updateReasonNavigation);
    document.querySelectorAll('.kh-our-story .kh-journey').forEach(root => {
      const track = root.querySelector('[data-kh-journey-track]');
      if (!track || !track.children.length || initializedTracks.has(track)) return;
      initializedTracks.add(track);
      root.dataset.khJourneyDesktop = String(desktop());
      let pending = false;
      track.addEventListener('scroll', () => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(() => { updateTimeline(root); pending = false; });
      }, {passive: true});
      updateTimeline(root);
      initializeJourneyWheel(root);
    });
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('.kh-our-story button');
    if (!button) return;
    if (button.matches('[data-kh-why-card]')) activateReason(button);
    if (button.matches('[data-kh-why-prev], [data-kh-why-next]')) {
      const cards = [...button.closest('.kh-why').querySelectorAll('[data-kh-why-card]')];
      const active = Math.max(0, cards.findIndex(card => card.classList.contains('is-active')));
      const step = button.matches('[data-kh-why-next]') ? 1 : -1;
      const target = cards[active + step];
      if (target) activateReason(target);
    }
    const root = button.closest('.kh-journey');
    if (!root) return;
    const {active} = timelineParts(root);
    if (button.matches('[data-kh-journey-prev]')) goToMilestone(root, active - 1);
    if (button.matches('[data-kh-journey-next]')) goToMilestone(root, active + 1);
    if (button.matches('[data-kh-journey-dot]')) goToMilestone(root, Number(button.dataset.index));
  });
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.querySelector('.kh-our-story .kh-journey');
    if (!root) return;
    const cleanupWheel = journeyWheelCleanups.get(root);
    if (cleanupWheel) cleanupWheel();
  });
  document.addEventListener('shopify:block:select', event => {
    if (!event.target.closest('.kh-our-story')) return;
    const card = event.target.closest('[data-kh-why-card]');
    if (card) activateReason(card);
    const milestone = event.target.closest('[data-kh-journey-item]');
    if (milestone) {
      const root = milestone.closest('.kh-journey');
      goToMilestone(root, timelineParts(root).items.indexOf(milestone));
    }
  });
  window.addEventListener('resize', () => {
    document.querySelectorAll('.kh-our-story .kh-why').forEach(root => {
      updateReasonNavigation(root);
      const active = root.querySelector('[data-kh-why-card].is-active');
      if (active) revealReason(active, 'auto');
    });
    document.querySelectorAll('.kh-our-story .kh-journey').forEach(root => {
      const track = root.querySelector('[data-kh-journey-track]');
      if (!track || !track.children.length) return;
      const wasDesktop = root.dataset.khJourneyDesktop === 'true';
      const savedIndex = Number(root.dataset.khJourneyIndex) || 0;
      root.dataset.khJourneyDesktop = String(desktop());
      if (desktop() && !wasDesktop) goToMilestone(root, savedIndex, 'auto');
      updateTimeline(root);
    });
  });
  initialize();
})();
